import { Salutation } from "../../../clients/graphqlTypes.js";
import { useTranslate } from "@tolgee/react";
import { SelectField, SelectFieldProps, SelectOption } from "./SelectField.js";

interface Props<Value extends string = "mr" | "ms">
  extends Omit<SelectFieldProps<Value>, "options" | "label"> {
  name: string;
  options?: { value: string; label: string }[];
}

/**
 * UserTitleField component that accepts dynamic salutation options.
 *
 * Defaults to hardcoded "mr" | "ms" values for backward compatibility,
 * but can accept any string-based salutation values (e.g., from Sap4Hana).
 *
 * @example
 * // Use default Mr./Ms. options
 * <UserTitleField name="title" />
 *
 * // Use dynamic options from Sap4Hana
 * <UserTitleField
 *   name="salutation"
 *   options={[
 *     { value: "0001", label: "Ms" },
 *     { value: "0002", label: "Mr" },
 *   ]}
 * />
 */
export const UserTitleField = <Value extends string = "mr" | "ms">({
  name,
  options: customOptions,
  ...otherProps
}: Props<Value>) => {
  const { t } = useTranslate(["Global"]);

  const defaultSalutations: { id: string; label: string; value: Value }[] = [
    {
      id: "mr",
      label: t("Mr", { ns: "Global" }),
      value: "mr" as Value,
    },
    {
      id: "ms",
      label: t("Ms", { ns: "Global" }),
      value: "ms" as Value,
    },
  ];

  const customSalutations = customOptions?.map(s => ({
    id: s.value,
    label: s.label,
    value: s.value as Value,
  }));

  const options = (customSalutations ?? defaultSalutations) as Array<
    SelectOption<Value> & { id: string }
  >;

  return (
    <SelectField
      label={t("Salutation", {
        ns: "Global",
      })}
      name={name}
      options={options}
      {...otherProps}
    />
  );
};
